import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const [accessToken, setAccessToken] = useState(null);

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token && token !== "undefined") {
				try {
					const parsedToken = JSON.parse(token);
					if (parsedToken) {
						config.headers["Authorization"] = `Bearer ${parsedToken}`;
					}
				} catch (err) {
					console.error("Invalid token in interceptor:", err);
				}
			}
			return config;
		},
		error => {
			return Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;

			// Verificar se a requisição que falhou já era o refresh token para evitar loop infinito
			if (originalRequest.url.endsWith("/auth/refresh_token")) {
				return Promise.reject(error);
			}

			// Tentar refresh para ambos 401 e 403
			if ((error?.response?.status === 401 || error?.response?.status === 403) && !originalRequest._retry) {
				originalRequest._retry = true;

				try {
					console.log(`[Auth] Attempting token refresh due to ${error.response.status}...`);
					const { data } = await api.post("/auth/refresh_token");
					if (data) {
						localStorage.setItem("token", JSON.stringify(data.token));
						api.defaults.headers.Authorization = `Bearer ${data.token}`;
						setAccessToken(data.token); // Update state to trigger socket reconnect
						originalRequest.headers.Authorization = `Bearer ${data.token}`; // Update original request header
						console.log("[Auth] Token refreshed successfully");
						return api(originalRequest);
					}
				} catch (refreshErr) {
					console.error("[Auth] Failed to refresh token:", refreshErr);
					setIsAuth(false);
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					history.push("/login");
				}
			}

			return Promise.reject(error);
		}
	);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
					setAccessToken(data.token);
				} catch (err) {
					console.error("Initial refresh failed:", err);
					setIsAuth(false);
					setUser({});
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
				}
			} else {
				setIsAuth(false);
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (loading || !accessToken) return;

		console.log("[Auth] Initializing WebSocket connection");
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			console.log("[Auth] Component unmounting - disconnecting socket");
			socket.disconnect();
		};
	}, [accessToken, loading]);

	const handleLogin = async (userData, rememberMe) => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", { ...userData, rememberMe });
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setAccessToken(data.token);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
